import { useMemo, useState } from 'react'
import { Check, ChevronsUpDown, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import type { Customer } from '@/lib/api/types'
import { customerIdentityLabel, customerIdentityValue } from '@/lib/customer-identity'

function customerLabel(customer: Customer) {
  return customerIdentityLabel(customer)
}

function customerSubtext(customer: Customer) {
  return [
    customer.is_placeholder ? 'Provisional identity' : null,
    customer.contact_person,
    customerIdentityValue(customer, 'email'),
    customerIdentityValue(customer, 'phone'),
  ]
    .filter(Boolean)
    .join(' · ')
}

export function CustomerCombobox({
  customers,
  value,
  onValueChange,
  disabled,
  placeholder = 'Search customers',
  emptyLabel = 'No customers found',
  id,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: {
  customers: Customer[]
  value: string
  onValueChange: (value: string) => void
  disabled?: boolean
  placeholder?: string
  emptyLabel?: string
  id?: string
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const selectedCustomer = customers.find((customer) => customer.id === value) ?? null
  const filteredCustomers = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()
    if (!normalizedQuery) return customers

    return customers.filter((customer) =>
      [
        customer.name,
        customer.customer_number,
        customer.company_name,
        customer.contact_person,
        customer.email,
        customer.phone,
        customer.tin_number,
      ].some((field) =>
        String(field || '')
          .toLowerCase()
          .includes(normalizedQuery),
      ),
    )
  }, [customers, query])

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (!nextOpen) setQuery('')
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="field"
          role="combobox"
          aria-expanded={open}
          aria-invalid={ariaInvalid || undefined}
          aria-describedby={ariaDescribedBy}
          disabled={disabled}
          className="w-full justify-between"
        >
          <span className={cn('truncate text-left', !selectedCustomer && 'text-muted-foreground')}>
            {selectedCustomer ? customerLabel(selectedCustomer) : placeholder}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={placeholder}
            className="h-8 border-0 px-0 shadow-none focus-visible:ring-0"
          />
        </div>
        <div role="listbox" className="max-h-72 overflow-y-auto p-1">
          {filteredCustomers.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">{emptyLabel}</div>
          ) : (
            filteredCustomers.map((customer) => {
              const selected = customer.id === value
              return (
                <button
                  key={customer.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className="flex w-full items-start gap-2 rounded-sm px-2 py-2 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                  onClick={() => {
                    onValueChange(customer.id)
                    setOpen(false)
                    setQuery('')
                  }}
                >
                  <Check className={cn('mt-0.5 h-4 w-4', selected ? 'opacity-100' : 'opacity-0')} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{customerLabel(customer)}</span>
                    {customerSubtext(customer) && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {customerSubtext(customer)}
                      </span>
                    )}
                  </span>
                </button>
              )
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
